import { Briefcase, Building2, CircleHelp, LogOut, User, Calendar, Timer, Send, Settings, Megaphone, ChartNoAxesCombined, Users, BookOpen, PanelRightOpen, PanelRightClose, Trash2 } from "lucide-react";
import React, { useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@workos-inc/authkit-react";
import { hasAnyAdminAccess } from "@/shared/config/adminPermissions";
import { routeToView, viewToRoute, type ViewMode } from "@/shared/config/routes";
import { BrandLogo } from "@/shared/components/BrandLogo";
import { Tabs, TabsList, TabsTrigger } from "@/shared/components/ui/tabs";
import { ProfileMenu, type ProfileMenuItem } from "@/shared/components/ProfileMenu/ProfileMenu";
import { Button } from "@/shared/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/shared/components/ui/tooltip";
import { useSettingsCache } from "@/shared/cache/slices/settings";
import { useReportsCache } from "@/shared/cache/slices/reports";
import { useUpdateNotification } from "@/shared/hooks/useUpdateNotification";

import {
	Sidebar as ShadcnSidebar,
	SidebarContent,
	SidebarFooter,
	SidebarGroup,
	SidebarGroupContent,
	SidebarHeader,
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
	useSidebar,
} from "@/shared/components/ui/sidebar";


type View = ViewMode;

const Sidebar: React.FC = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const { user, signOut } = useAuth();
	const showAdminLink = hasAnyAdminAccess(user?.email);
	const { settings } = useSettingsCache();
	const { reports } = useReportsCache();
	const { hasNewUpdate, dismissUpdate } = useUpdateNotification();
	const { state, toggleSidebar } = useSidebar();
	const collapsed = state === "collapsed";

	const viewMode: View = useMemo(() => {
		const segment = location.pathname.split('/').filter(Boolean)[0] || 'tracked';
		return routeToView[segment] || 'entries';
	}, [location.pathname]);

	const userInitials = useMemo(() => {
		if (user?.firstName && user?.lastName) {
			return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
		}
		if (user?.email) {
			return user.email[0].toUpperCase();
		}
		return "?";
	}, [user]);

	const userDisplayName = useMemo(() => {
		if (user?.firstName && user?.lastName) {
			return `${user.firstName} ${user.lastName}`;
		}
		return user?.email ?? "";
	}, [user]);

	const navigateTo = (view: View) => {
		navigate(viewToRoute[view] + location.search);
	};

	const hasDirectReports = reports.manages.length > 0;

	const navItems: Array<{
		view: View;
		label: string;
		icon: React.ElementType;
	}> = [
		{ view: "entries", label: "Tracked", icon: Timer },
		{ view: "deleted", label: "Deleted", icon: Trash2 },
		{ view: "history", label: "Exported", icon: Send },
		{ view: "calendar", label: "Calendar", icon: Calendar },
		{ view: "reporting" as View, label: "Reporting", icon: ChartNoAxesCombined },
		...(hasDirectReports
			? [{ view: "team" as View, label: "Team", icon: Users }]
			: []),
	];

	const profileMenuItems: ProfileMenuItem[] = [
		{ label: "Settings", icon: Settings, isActive: viewMode === "settings", onClick: () => navigateTo("settings") },
		{ label: "Support", icon: User, isActive: viewMode === "support", onClick: () => navigateTo("support") },
		{ label: "FAQ", icon: CircleHelp, isActive: viewMode === "faq", onClick: () => navigateTo("faq") },
		{ label: "Docs", icon: BookOpen, isActive: viewMode === "docs", onClick: () => navigateTo("docs"), separator: true } as ProfileMenuItem,
		{ label: "Updates", icon: Megaphone, isActive: viewMode === "updates", onClick: () => { dismissUpdate(); navigateTo("updates"); }, badge: hasNewUpdate },
		{ label: "Sign out", icon: LogOut, onClick: () => signOut({ returnTo: window.location.origin }), separator: true },
	];

	return (
		<ShadcnSidebar
			collapsible="icon"
			className="bg-sidebar border-r border-sidebar-border [&_[data-sidebar=sidebar]]:bg-sidebar"
		>
			<SidebarHeader className="px-4 pt-7 pb-2 text-sidebar-foreground group-data-[collapsible=icon]:px-0">
				<div className="flex h-7 items-center justify-between gap-2 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0">
					<BrandLogo
						className="h-7 w-auto group-data-[collapsible=icon]:hidden"
						aria-label="Hourglass"
					/>
					<Button
						variant="ghost"
						size="icon"
						onClick={toggleSidebar}
						aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
						className="mb-1 size-9 text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-foreground [&_svg]:size-5"
					>
						{collapsed ? <PanelRightClose /> : <PanelRightOpen />}
					</Button>
				</div>
				{showAdminLink && collapsed && (
					<Tooltip>
						<TooltipTrigger asChild>
							<Button
								variant="ghost"
								size="icon"
								onClick={() => navigate("/admin")}
								aria-label="Switch to Admin view"
								className="mx-auto mt-3 hidden size-9 text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-foreground group-data-[collapsible=icon]:flex [&_svg]:size-5"
							>
								<Building2 />
							</Button>
						</TooltipTrigger>
						<TooltipContent side="right">Switch to Admin</TooltipContent>
					</Tooltip>
				)}
				{showAdminLink && (
					<div className="mt-2 -mx-6 px-4 group-data-[collapsible=icon]:hidden">
						<Tabs
							defaultValue="timekeeper"
							onValueChange={(value) => {
								if (value === "admin") navigate("/admin");
							}}
						>
							<TabsList className="flex bg-transparent rounded-none border-b border-sidebar-border w-full gap-0 p-0 h-auto">
								<TabsTrigger value="timekeeper" className="flex-1 rounded-none border-b-2 border-transparent bg-transparent px-2 py-1.5 text-xs text-sidebar-foreground/60 shadow-none transition-none data-[state=active]:border-b-primary data-[state=active]:bg-transparent data-[state=active]:text-sidebar-foreground data-[state=active]:shadow-none gap-1.5">
									<Briefcase className="size-3.5" />
									Timekeeper
								</TabsTrigger>
								<TabsTrigger value="admin" className="flex-1 rounded-none border-b-2 border-transparent bg-transparent px-2 py-1.5 text-xs text-sidebar-foreground/60 shadow-none transition-none data-[state=active]:border-b-primary data-[state=active]:bg-transparent data-[state=active]:text-sidebar-foreground data-[state=active]:shadow-none gap-1.5">
									<Building2 className="size-3.5" />
									Admin
								</TabsTrigger>
							</TabsList>
						</Tabs>
					</div>
				)}
			</SidebarHeader>

			<SidebarContent>
				<SidebarGroup>
					<SidebarGroupContent>
						<SidebarMenu>
							{navItems.map((item) => (
								<SidebarMenuItem key={item.view}>
									<SidebarMenuButton
										isActive={viewMode === item.view}
										tooltip={item.label}
										onClick={() => navigateTo(item.view)}
										className="h-10 px-3 gap-3 data-[active=true]:font-normal [&>svg]:size-5"
									>
										<item.icon />
										<span>{item.label}</span>
									</SidebarMenuButton>
								</SidebarMenuItem>
							))}
						</SidebarMenu>
					</SidebarGroupContent>
				</SidebarGroup>
			</SidebarContent>

			<SidebarFooter>
				<ProfileMenu
					userDisplayName={userDisplayName}
					userInitials={userInitials}
					userEmail={user?.firstName ? user.email : undefined}
					subtitle={settings?.prof_type || undefined}
					menuItems={profileMenuItems}
				/>
			</SidebarFooter>
		</ShadcnSidebar>
	);
};

export default Sidebar;
